<template>
  <div class="tw-flex tw-flex-col sm:tw-items-center tw-justify-center tw-h-screen tw-bg-gray-100">
    <base-card shadow no-padding class="tw-overflow-hidden tw-w-full sm:tw-max-w-sm">
      <div class="tw-py-8 tw-flex tw-justify-center tw-mb-6" :class="{ 'tw-bg-primary-500': logo === 'rx-harmony-logo' }">
        <component :is="logo" class="tw-w-3/4 tw-max-h-32" />
      </div>
      <base-content>
        <div class="tw-text-lg tw-text-gray-800 tw-mb-4 tw-text-center">Reset your password</div>
        <div
          class="tw-w-full tw-px-3 sm:tw-max-w-md tw-rounded-lg tw-bg-red-50 tw-text-red-600 tw-text-sm tw-font-normal tw-border tw-border-red-500 tw-py-3 tw-text-center tw-flex tw-mb-5"
          v-if="!!errorMessage"
        >
          <alert-icon class="tw-h-6 tw-w-6 tw-text-red-600 tw-mr-2" />
          <span class="tw-my-auto">{{ errorMessage }}</span>
        </div>
        <div
          v-if="requestVerificationCode"
          class="tw-w-full tw-px-3 sm:tw-max-w-md tw-rounded-lg tw-bg-primary-50 tw-text-primary-600 tw-text-sm tw-font-normal tw-border tw-border-primary-500 tw-py-3 tw-text-center tw-flex tw-mb-5"
        >
          Check your email for a verification code.
        </div>
        <base-form ref="form" v-model="isFormValid" @submit.prevent>
          <base-text-field label="Email" type="email" placeholder="Enter your email" v-model="email" @keyup.enter="submit()" required />
          <div v-if="requestVerificationCode">
            <base-text-field label="New password" type="password" placeholder="Enter your new password" v-model="newPassword" required class="tw-mt-2" />
            <base-text-field
              label="Confirm password"
              type="password"
              placeholder="Confirm your new password"
              v-model="confirmNewPassword"
              :rules="passwordRules"
              required
              class="tw-mt-2"
            />
            <base-text-field label="Verification code" placeholder="Enter your verification code" v-model="verificationCode" required class="tw-mt-2" @keyup.enter="submit()" />
          </div>
          <base-button class="tw-w-full" @click="submit()" :loading="isLoading" size="lg" :disabled="!isFormValid">
            <span v-if="!requestVerificationCode">Send Email</span>
            <span v-if="requestVerificationCode">Update Password</span>
          </base-button>
          <div class="tw-flex tw-justify-center tw-mt-6">
            <base-button type="text" @click="href('/signin')">
              Sign In
            </base-button>
            <span class="tw-mx-2">|</span>
            <base-button type="text">
              <a href="https://www.predictile.com/support" target="_blank">Support</a>
            </base-button>
          </div>
        </base-form>
      </base-content>
    </base-card>
  </div>
</template>

<script>
import PredictileLogo from "@/assets/images/predictile-logo-square.svg";
import RxHarmonyLogo from "@/assets/images/rx-harmony-logo.svg";
import cognito from "@/configuration/cognito";
import { mapGetters } from "vuex";
import { AlertIcon } from "@/assets/svgs";

export default {
  components: {
    PredictileLogo,
    RxHarmonyLogo,
    AlertIcon,
  },
  data() {
    return {
      isLoading: false,
      email: null,
      newPassword: null,
      confirmNewPassword: null,
      verificationCode: null,
      requestVerificationCode: false,
      errorMessage: null,
      isFormValid: true,
      passwordRules: [() => this.newPassword === this.confirmNewPassword || "Please enter matching passwords"],
    };
  },
  computed: {
    ...mapGetters("organization", ["cobrandId"]),
    logo() {
      switch (this.cobrandId) {
        case "rx-harmony":
          return "rx-harmony-logo";
      }
      return "predictile-logo";
    },
  },
  methods: {
    async submit() {
      if (!this.$refs.form.validate()) return;
      this.isLoading = true;

      const options = {
        email: this.email,
        onLoading: () => {
          this.isLoading = true;
          this.errorMessage = null;
        },
        onFailure: async (res) => {
          // If this specific Cognito error, fallback to custom API
          if (res.message === "User password cannot be reset in the current state.") {
            try {
              await this.$axios.post(`/businesses/users/${this.email}/send-forgot-password-email`);
              this.requestVerificationCode = false;
              this.errorMessage = null;
              this.$refs.form.reset();
              this.isLoading = false;
              this.$router.push({
                path: "/signin/forgot/success",
                query: {
                  message: "We've sent a password reset email. Please check your inbox to continue.",
                },
              });
            } catch (error) {
              this.errorMessage = (error && error.response && error.response.data && error.response.data.message) || "Something went wrong while sending the reset email.";
              this.isLoading = false;
            }
            return;
          }

          // Show any other Cognito error
          this.errorMessage = res.message;
          this.isLoading = false;
        },
        onSuccess: () => {
          if (!this.requestVerificationCode) {
            this.requestVerificationCode = true;
            this.isLoading = false;
            this.errorMessage = null;
            return;
          }
          this.$router.push({
            path: "/signin/forgot/success",
            query: {
              message: "Your password was successfully updated.",
            },
          });
        },
      };

      if (!this.requestVerificationCode) {
        // Phase 1: Try Cognito first
        cognito.forgotPassword(options);
      } else {
        // Phase 2: Verify code & update password
        cognito.forgotPassword({
          ...options,
          verificationCode: this.verificationCode,
          newPassword: this.newPassword,
        });
      }
    },
    href(path) {
      this.$router.push(path);
    },
  },
};
</script>
